import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './FakeReceiptBuilder.scss';

const FakeReceiptBuilder = ({
  storeName = 'Madison Bicycle Shop',
  receiptTitle = 'SALES RECEIPT (DEMO)',
  receiptNo = 'R-20250528-0001',
  date = 'June 03, 2025',
  paymentMethod = 'VISA •••• 3333',
  status = 'Approved',
  items = [
    { name: 'Bicycle Frame', qty: 1, price: 100.0 },
    { name: 'Helmet', qty: 1, price: 300.0 },
    { name: 'U-Lock', qty: 1, price: 100.0 },
  ],
  address = '123 Madison Ave, Toronto, ON',
  supportEmail = 'support@madisonbikeshop.example',
  currency = 'CAD',
  tpsRate = 5,
  tvqRate = 9.975,
}) => {
  // Reference to the receipt element for printing
  const receiptRef = useRef(null);

  // Local state for inline editing
  const [form, setForm] = useState({
    storeName,
    receiptTitle,
    receiptNo,
    date,
    paymentMethod,
    status,
    items: items.map((i) => ({ ...i })),
    address,
    supportEmail,
    currency,
    tpsRate,
    tvqRate,
  });

  // Calculate subtotal and taxes
  const subtotal = form.items.reduce(
    (sum, item) => sum + Number(item.price || 0) * Number(item.qty || 0),
    0
  );
  const tpsAmount = subtotal * (Number(form.tpsRate) / 100 || 0);
  const tvqAmount = (subtotal + tpsAmount) * (Number(form.tvqRate) / 100 || 0);
  const totalTax = tpsAmount + tvqAmount;
  const total = subtotal + totalTax;

  // Format currency
  const formatCurrency = (amount) => {
    return `$${amount.toFixed(2)}`;
  };

  // Form handlers
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        name === 'tpsRate' || name === 'tvqRate'
          ? value === '' ? '' : Number(value)
          : value,
    }));
  };

  const handleItemChange = (index, field, value) => {
    setForm((prev) => {
      const next = {
        ...prev,
        items: prev.items.map((it, i) => (i === index ? { ...it, [field]: field === 'qty' || field === 'price' ? Number(value) : value } : it)),
      };
      return next;
    });
  };

  const addItem = () => {
    setForm((prev) => ({ ...prev, items: [...prev.items, { name: '', qty: 1, price: 0 }] }));
  };

  const removeItem = (index) => {
    setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  // Handle printing of the receipt
  const handlePrint = () => {
    const printContent = receiptRef.current;
    const windowUrl = 'about:blank';
    const windowName = 'Print Receipt';
    const windowFeatures = 'width=800,height=900,top=100,left=100';

    const printWindow = window.open(windowUrl, windowName, windowFeatures);

    if (!printWindow) return;

    const doc = printWindow.document;
    doc.title = 'Print Receipt';

    // Inject styles
    const styleEl = doc.createElement('style');
    styleEl.textContent = `
      @media print {
        body { margin: 0; padding: 0; }
        .print-button { display: none !important; }
      }
      body {
        background: #f5f5f5;
        margin: 0;
        padding: 24px;
        font: 14px/1.35 -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
        color: #111;
      }
      .paper {
        width: 380px;
        margin: 0 auto;
        background: #fff;
        padding: 24px 20px;
        box-shadow: 0 6px 18px rgba(0,0,0,.08);
        position: relative;
        overflow: hidden;
      }
      h1 { font-size: 18px; margin: 0 0 6px; }
      h2 { font-size: 13px; margin: 0 0 10px; color: #666; }
      .row { display: flex; justify-content: space-between; margin: 8px 0; }
      .divider { border-top: 1px dashed #aaa; margin: 12px 0; }
      .items .row { align-items: center; }
      .muted { color: #666; }
      .totals .row { font-weight: 600; }
      .footer { margin-top: 14px; font-size: 12px; color: #666; }
      .mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; }
      .item-name { flex: 1; }
      .item-qty { width: 36px; text-align: right; }
      .item-price { width: 70px; text-align: right; }
      .item-total { width: 80px; text-align: right; }
      .print-button-container { text-align: center; margin-top: 20px; }
      .print-button-container button {
        padding: 8px 16px;
        background: #0078d4;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
      }
    `;
    doc.head.appendChild(styleEl);

    // Inject content
    const wrapper = doc.createElement('div');
    wrapper.innerHTML = `${printContent.outerHTML}
      <div class="print-button-container">
        <button onclick="window.print(); window.close();">Print Receipt</button>
      </div>`;
    doc.body.appendChild(wrapper);
  };

  return (
    <div className='receipt-container'>
      <details className='editor'>
        <summary>Edit demo data</summary>
        <div className='editor-grid'>
          <label>
            <span>Store name</span>
            <input name='storeName' value={form.storeName} onChange={handleFieldChange} />
          </label>
          <label>
            <span>Receipt title</span>
            <input name='receiptTitle' value={form.receiptTitle} onChange={handleFieldChange} />
          </label>
          <label>
            <span>Receipt No.</span>
            <input name='receiptNo' value={form.receiptNo} onChange={handleFieldChange} />
          </label>
          <label>
            <span>Date</span>
            <input name='date' value={form.date} onChange={handleFieldChange} />
          </label>
          <label>
            <span>Payment</span>
            <input name='paymentMethod' value={form.paymentMethod} onChange={handleFieldChange} />
          </label>
          <label>
            <span>Status</span>
            <select name='status' value={form.status} onChange={handleFieldChange}>
              <option>Approved</option>
              <option>Pending</option>
              <option>Declined</option>
            </select>
          </label>
          <label>
            <span>Currency</span>
            <select name='currency' value={form.currency} onChange={handleFieldChange}>
              <option>CAD</option>
              <option>USD</option>
              <option>EUR</option>
              <option>GBP</option>
            </select>
          </label>
          <label>
            <span>TPS %</span>
            <input
              name='tpsRate'
              type='number'
              step='0.001'
              min='0'
              value={form.tpsRate}
              onChange={handleFieldChange}
            />
          </label>
            <label>
              <span>TVQ %</span>
              <input
                name='tvqRate'
                type='number'
                step='0.001'
                min='0'
                value={form.tvqRate}
                onChange={handleFieldChange}
              />
            </label>
          <label className='wide'>
            <span>Address</span>
            <input name='address' value={form.address} onChange={handleFieldChange} />
          </label>
          <label className='wide'>
            <span>Support email</span>
            <input name='supportEmail' value={form.supportEmail} onChange={handleFieldChange} />
          </label>
        </div>

        <div className='item-editor'>
          <div className='items-header'>
            <div>Item</div>
            <div>Qty</div>
            <div>Price</div>
            <div></div>
          </div>
          {form.items.map((it, idx) => (
            <div className='item-row' key={idx}>
              <input placeholder='Description' value={it.name} onChange={(e) => handleItemChange(idx, 'name', e.target.value)} />
              <input type='number' min='0' step='1' value={it.qty} onChange={(e) => handleItemChange(idx, 'qty', e.target.value)} />
              <input type='number' min='0' step='0.01' value={it.price} onChange={(e) => handleItemChange(idx, 'price', e.target.value)} />
              <button type='button' className='secondary' onClick={() => removeItem(idx)}>
                Remove
              </button>
            </div>
          ))}
          <div className='add-item-row'>
            <button type='button' onClick={addItem}>
              + Add item
            </button>
          </div>
        </div>
      </details>
      <div className='print-button-wrapper'>
        <button className='print-button' onClick={handlePrint}>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='16'
            height='16'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
            className='print-icon'
          >
            <polyline points='6 9 6 2 18 2 18 9'></polyline>
            <path d='M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2'></path>
            <rect x='6' y='14' width='12' height='8'></rect>
          </svg>
          Print Receipt
        </button>
      </div>
      <div className='paper' ref={receiptRef}>
        <h1>{form.storeName}</h1>
        <h2>{form.receiptTitle}</h2>
        <div className='divider'></div>

        <div className='row'>
          <div>Receipt No.</div>
          <div className='mono'>{form.receiptNo}</div>
        </div>
        <div className='row'>
          <div>Date</div>
          <div>{form.date}</div>
        </div>
        <div className='row'>
          <div>Payment</div>
          <div>{form.paymentMethod}</div>
        </div>
        <div className='row'>
          <div>Status</div>
          <div>{form.status}</div>
        </div>

        <div className='divider'></div>

        <div className='row muted'>
          <div className='item-name'>Item</div>
          <div className='item-qty'>Qty</div>
          <div className='item-price'>Price</div>
          <div className='item-total'>Total</div>
        </div>

        <div className='items'>
          {form.items.map((item, index) => (
            <div className='row' key={index}>
              <div className='item-name'>{item.name}</div>
              <div className='item-qty'>{item.qty}</div>
              <div className='item-price'>{formatCurrency(item.price)}</div>
              <div className='item-total'>{formatCurrency(item.price * item.qty)}</div>
            </div>
          ))}
        </div>

        <div className='divider'></div>

        <div className='totals'>
          <div className='row'>
            <div>Subtotal</div>
            <div>{formatCurrency(subtotal)}</div>
          </div>
          <div className='row'>
            <div>TPS ({Number(form.tpsRate) || 0}%)</div>
            <div>{formatCurrency(tpsAmount)}</div>
          </div>
          <div className='row'>
            <div>TVQ ({Number(form.tvqRate) || 0}%)</div>
            <div>{formatCurrency(tvqAmount)}</div>
          </div>
          <div className='row'>
            <div>Total ({form.currency})</div>
            <div>{formatCurrency(total)}</div>
          </div>
        </div>

        <div className='divider'></div>

        <div className='footer'>
          {form.storeName} • {form.address}
          <br />
          Support: {form.supportEmail}
        </div>
      </div>
    </div>
  );
};

export default FakeReceiptBuilder;

FakeReceiptBuilder.propTypes = {
  storeName: PropTypes.string,
  receiptTitle: PropTypes.string,
  receiptNo: PropTypes.string,
  date: PropTypes.string,
  paymentMethod: PropTypes.string,
  status: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      qty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    })
  ),
  address: PropTypes.string,
  supportEmail: PropTypes.string,
  currency: PropTypes.string,
  tpsRate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tvqRate: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
